import { LibraryCreatedProductOut } from '../../../../store/apiLibx';
import { useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import { Genres } from './types';
import { GenreOut } from '../../../../store/api';
import { mapAgeCategory } from '../../../../yourborrowbox/localcontent/product/api/mappings/ageCategory';
import { mapAgeGroup } from '../../../../yourborrowbox/localcontent/product/api/mappings/ageGroup';
import { mapFiction } from '../../../../yourborrowbox/localcontent/product/api/mappings/fiction';
import { mapGenreCategory } from '../../../../yourborrowbox/localcontent/product/api/mappings/genreCategory';
import { mapExplicit } from '../../../../yourborrowbox/localcontent/product/api/mappings/explicit';
import { mapLanguage } from '../../../../yourborrowbox/localcontent/product/api/mappings/language';
import { mapProductFormat } from '../../../../yourborrowbox/localcontent/product/api/mappings/productFormat';
import isNil from 'lodash/isNil';
import { useLocalContentProduct } from './useLocalContentProduct';
import { useLocalContentGenreCategories } from './useLocalContentGenreCategories';
import { useLocalContentGenres } from './useLocalContentGenres';
import { useStandardGenres } from './useStandardGenres';
import { mapGenres } from '../../../../yourborrowbox/localcontent/product/api/mappings/mapGenres';
import { mapAuthors } from '../../../../yourborrowbox/localcontent/product/api/mappings/mapAuthors';
import { useTranslation } from 'react-i18next';
import { localiseGenresListMapper } from './genreLocalisationMapper';
import { validateDate } from '../../../../shared/utils/formatDate';

function stringToBoolean(str) {
    if (str === 'true') {
        return true;
    } else if (str === 'false') {
        return false;
    } else {
        return str;
    }
}

export interface AdditionalProps {
    seriesNumberError?: boolean;
    releaseDateError?: boolean;
    pagesError?: boolean;
}

export const useUpdateForm = (productId: string | undefined) => {
    const [showAddPerformerPopup, setShowAddPerformerPopup] = useState(false);
    const [localGenreSearchQuery, setLocalGenreSearchQuery] = useState('');
    const [standardGenreSearchQuery, setStandardGenreSearchQuery] = useState('');
    const {
        t,
        i18n: { language: currentLanguage }
    } = useTranslation();
    const [productState, setProductState] = useState<(LibraryCreatedProductOut & AdditionalProps) | undefined>({
        seriesNumberError: false,
        releaseDateError: false,
        pagesError: false,
        releaseDate: t('dateFormat.date', {
            val: new Date().getTime()
        }),
        enableMarcRecord: false
    });

    const { data: product, isInitialLoading: isProductLoading } = useLocalContentProduct(productId ?? '', {
        enabled: !!productId,
        onSuccess: product => {
            setProductState({
                ...product,
                seriesNumberError: false,
                releaseDateError: false,
                pagesError: false,
                releaseDate: product.releaseDate
                    ? t('dateFormat.date', {
                          val: new Date(product.releaseDate)
                      })
                    : undefined
            });
        }
    });

    const { data: categories, isInitialLoading: isCategoriesLoading } = useLocalContentGenreCategories(
        { fiction: stringToBoolean(productState?.fiction) },
        {
            enabled: productState?.fiction !== undefined && productState?.fiction !== null && !isProductLoading
        }
    );

    const localisedCategories = useMemo(() => {
        return Object.keys(categories ?? {}).reduce((acc, key) => {
            acc[key] = t(`filters:genre.${key}`, categories![key]);
            return acc;
        }, {});
    }, [categories, t]);

    const genreCategoryData = mapGenreCategory(productState?.genreCategory, localisedCategories, t);
    const { productFormatData } = mapProductFormat(productState?.productFormat!, t);
    const currentLanguageLocalised = useMemo(() => {
        return t(
            `filters:language.${productState?.language}`,
            productState?.language ?? t('yourLocalContent.yourTitles.select')
        );
    }, [productState?.language, t]);
    const { languageData } = mapLanguage(productState?.languageCode, currentLanguageLocalised) ?? {};

    const { ageCategoryData } = useMemo(
        () => mapAgeCategory(productState?.ageCategory, t),
        [productState?.ageCategory, t]
    );
    const { ageGroupData } = useMemo(
        () => mapAgeGroup(productState?.ageGroup, ageCategoryData, t),
        [ageCategoryData, productState?.ageGroup, t]
    );
    const { fictionData } = mapFiction(productState?.fiction, t);
    const { explicitData } = mapExplicit(productState?.explicit, t);
    const authors = mapAuthors(productState?.authors) ?? [];
    const authorIds = productState?.authors?.map(author => author.id ?? '') || [];

    const { data: localGenreList, isInitialLoading: isLocalContentGenresLoading } = useLocalContentGenres({
        fiction: productState?.fiction ?? product?.fiction,
        category: productState?.genreCategory?.id ?? product?.genreCategory?.id ?? '',
        searchQuery: localGenreSearchQuery,
        enabled: !!productState?.genreCategory && productState?.fiction !== null && !isCategoriesLoading
    });

    const { data: standardGenreList, isInitialLoading: isStandardGenresLoading } = useStandardGenres(
        productState?.fiction ?? product?.fiction,
        productState?.genreCategory?.id ?? product?.genreCategory?.id ?? '',
        standardGenreSearchQuery
    );

    const translatedStandardGenreList = useMemo(() => {
        return {
            genres: localiseGenresListMapper(standardGenreList, t),
            mandatory: standardGenreList?.mandatory
        };
    }, [standardGenreList, t]);

    const translatedLocalGenreList = useMemo(() => {
        return {
            genres: localiseGenresListMapper(localGenreList, t),
            mandatory: localGenreList?.mandatory
        };
    }, [localGenreList, t]);

    const { localGenres, standardGenres } = useMemo(() => mapGenres(product || {}, t), [product, t]);

    const [genres, setGenres] = useState<Genres>({
        localGenres,
        standardGenres
    });

    const isSeriesNumberValid = (value: string) => {
        const decimalValue = Number(value);
        return decimalValue % 1 !== 0;
    };

    const setSeriesNumber = useCallback(value => {
        const seriesNumber = value;
        const emptyValueString = seriesNumber.length === 0;

        if (emptyValueString) {
            setProductState(prevProductState => {
                return { ...prevProductState, seriesNumber, seriesNumberError: false };
            });
            return;
        }

        if (seriesNumber && !isNaN(Number(seriesNumber)) && !isSeriesNumberValid(seriesNumber)) {
            setProductState(prevProductState => {
                return { ...prevProductState, seriesNumber, seriesNumberError: false };
            });
        } else {
            setProductState(prevProductState => {
                return { ...prevProductState, seriesNumber, seriesNumberError: true };
            });
        }
    }, []);

    const setProductStateWithValue = useCallback((key, value) => {
        setProductState(prevProductState => {
            const product = { ...prevProductState };
            product[key] = value;
            return { ...product };
        });
    }, []);

    const validateReleaseDate = useCallback(() => {
        const isValidDate = validateDate(productState?.releaseDate as string, currentLanguage);
        setProductState(prevProductState => {
            return { ...prevProductState, releaseDateError: !isValidDate };
        });
    }, [productState?.releaseDate, currentLanguage]);

    const onRemoveKeyword = useCallback((index: number) => {
        if (index < 0) {
            return;
        }
        setProductState(prevProductState => {
            const keywords = prevProductState?.keywords ?? [];
            keywords.splice(index, 1);
            return {
                ...prevProductState,
                keywords
            };
        });
    }, []);

    const onAddKeyword = useCallback((keyword: string, cb: () => void) => {
        if (isEmpty(keyword)) {
            return Promise.reject();
        }
        setProductState(prevProductState => {
            const keywords = prevProductState?.keywords ?? [];
            if (keywords.includes(keyword)) {
                return;
            }
            keywords.push(keyword);
            return {
                ...prevProductState,
                keywords
            };
        });

        cb();
        return Promise.resolve();
    }, []);

    const onAddPerformer = useCallback(performer => {
        setProductState(prevProductState => {
            return {
                ...prevProductState,
                authors: [...(prevProductState?.authors ?? []), performer]
            };
        });
        setShowAddPerformerPopup(false);
    }, []);

    const onRemovePerformer = useCallback(
        performerId => {
            const performers = filter(productState?.authors, a => a.id !== performerId);
            setProductState(prevProductState => {
                return {
                    ...prevProductState,
                    authors: mapAuthors(performers)
                };
            });
        },
        [productState?.authors]
    );

    const setPages = useCallback(value => {
        const pagesAmount = value;
        const emptyValueString = pagesAmount.length === 0;

        if (emptyValueString) {
            setProductState(prevProductState => {
                return { ...prevProductState, pages: pagesAmount, pagesError: true };
            });
            return;
        }

        if (pagesAmount && !isNaN(Number(pagesAmount))) {
            setProductState(prevProductState => {
                return { ...prevProductState, pages: pagesAmount, pagesError: false };
            });
        } else {
            setProductState(prevProductState => {
                return { ...prevProductState, pages: pagesAmount, pagesError: true };
            });
        }
    }, []);

    const setAgeCategory = useCallback(
        (_, key) => {
            setProductState(prevProductState => {
                return { ...prevProductState, ageCategory: key, ageGroup: ageGroupData };
            });
        },
        [ageGroupData]
    );

    const setFiction = useCallback(
        (value: boolean, currentKey: boolean) => {
            let resetGenresAndCategory = false;
            if (productState?.fiction !== currentKey) {
                resetGenresAndCategory = true;
            }
            setProductState(prevProductState => {
                return {
                    ...prevProductState,
                    fiction: currentKey,
                    genres: resetGenresAndCategory ? [] : prevProductState?.genres
                };
            });
        },
        [productState?.fiction]
    );

    const setGenreCategory = useCallback(
        (value: string, currentKey: string) => {
            let resetGenres = false;
            if (productState?.genreCategory !== currentKey) {
                resetGenres = true;
            }
            setProductState(prevProductState => {
                return {
                    ...prevProductState,
                    genreCategory: { ...prevProductState?.genreCategory, current: value, id: currentKey, currentKey }
                };
            });

            setGenres(prevGenreState => {
                return {
                    ...prevGenreState,
                    localGenres: resetGenres ? [] : prevGenreState?.localGenres,
                    standardGenres: resetGenres ? [] : prevGenreState?.standardGenres
                };
            });
        },
        [productState?.genreCategory]
    );

    const addLocalGenre = useCallback((value: GenreOut) => {
        setGenres(prevGenreState => {
            return {
                ...prevGenreState,
                localGenres: [...(prevGenreState?.localGenres ?? []), value]
            };
        });
    }, []);

    const addStandardGenre = useCallback((value: GenreOut) => {
        setGenres(prevGenreState => {
            return {
                ...prevGenreState,
                standardGenres: [...(prevGenreState?.standardGenres ?? []), value]
            };
        });
    }, []);
    const removeLocalGenre = useCallback(
        (value: string | number) => {
            const updatedGenres = genres?.localGenres?.filter(element => element.id !== String(value));
            setGenres(prevGenreState => {
                return {
                    ...prevGenreState,
                    localGenres: updatedGenres
                };
            });
        },
        [genres?.localGenres]
    );

    const removeStandardGenre = useCallback(
        value => {
            const updatedGenres = genres?.standardGenres?.filter(element => element.id !== value);

            setGenres(prevGenreState => {
                return {
                    ...prevGenreState,
                    standardGenres: updatedGenres
                };
            });
        },
        [genres?.standardGenres]
    );

    const setExplicit = useCallback((value: boolean, currentKey: string) => {
        setProductState(prevProductState => {
            return { ...prevProductState, explicit: currentKey as unknown as boolean };
        });
    }, []);

    const setLanguage = useCallback((value: string, currentKey: string) => {
        setProductState(prevProductState => {
            return { ...prevProductState, language: value, languageCode: currentKey };
        });
    }, []);

    return {
        setProductStateWithValue,
        onRemoveKeyword,
        onAddKeyword,
        productState,
        genreCategory: genreCategoryData,
        productFormat: productFormatData,
        ageGroup: ageGroupData,
        fiction: fictionData,
        explicit: explicitData,
        ageCategory: ageCategoryData,
        authors,
        authorIds,
        genres: {
            localGenres: genres.localGenres ?? [],
            standardGenres: genres.standardGenres ?? []
        },
        releaseDateFormatted: isNil(productState?.releaseDate) ? '' : (productState?.releaseDate as string),
        onAddPerformer,
        onRemovePerformer,
        setPages,
        setExplicit,
        language: languageData,
        setAgeCategory,
        setFiction,
        setGenreCategory,
        addLocalGenre,
        addStandardGenre,
        removeLocalGenre,
        removeStandardGenre,
        validateReleaseDate,
        setSeriesNumber,
        setLocalGenreSearchQuery,
        setStandardGenreSearchQuery,
        standardGenreSearchQuery,
        localGenreSearchQuery,
        standardGenreList: translatedStandardGenreList ?? {},
        localGenreList: translatedLocalGenreList ?? {},
        showAddPerformerPopup,
        setShowAddPerformerPopup,
        isProductLoading,
        isLocalContentGenresLoading,
        isStandardGenresLoading,
        isCategoriesLoading,
        setLanguage
    };
};
